import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { TransactionService } from './transaction.service';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HelperService } from '../utils/helper.service';

@Injectable({
  providedIn: 'root'
})
export class FeeComponentService {
  feePeriodComponentsHolder = { 'feePeriodComponents': [] };
  private feeComponentsSubject = new Subject<any>();
  $feeComponentsObservable = this.feeComponentsSubject.asObservable();

  nonStandardFeeComponentsHolder = { 'nonStandardFeeComponents': [] };
  private nonStandardFeeComponentsSubject = new Subject<any>();
  $nonStandardFeeComponentsObservable = this.nonStandardFeeComponentsSubject.asObservable();

  constructor(
    private api: ApiService,
    private transactionService: TransactionService,
    private helperService: HelperService) { }

  getByTransactionId(transactionId: number, transactionType) {
    const endpoint = this.api.getTransactionEndpoint({ 'transaction_type': transactionType });
    return this.api.get(`/api/transactions/${transactionId}/fee_component/`)
    .pipe(
      map(response => {
        this.nonStandardFeeComponentsHolder.nonStandardFeeComponents = response.fee_component;
        this.nonStandardFeeComponentsSubject.next(this.nonStandardFeeComponentsHolder.nonStandardFeeComponents);
        return response.fee_component;
      })
    );
  }

  saveFeeComponent(transactionId: number, feeComponentId: number, feeComponent, transactionType) {
    const endpoint = this.api.getTransactionEndpoint({ 'transaction_type': transactionType });
    this.nonStandardFeeComponentsHolder.nonStandardFeeComponents.forEach(_nonStdFeePeriodComponent => {
      if (feeComponentId === _nonStdFeePeriodComponent.id) {
        Object.assign(_nonStdFeePeriodComponent, feeComponent);
        this.nonStandardFeeComponentsSubject.next(this.nonStandardFeeComponentsHolder.nonStandardFeeComponents);
      }
    });
    return this.api.put(`/api/transactions/${transactionId}/fee_component/${feeComponentId}/`, feeComponent);
  }

  newFeeComponent(transactionId: number, transactionType) {
    const endpoint = this.api.getTransactionEndpoint({ 'transaction_type': transactionType });
    return this.api.post(`/api/transactions/${transactionId}/fee_component/`, { 'fee_type': '' });
  }

  deleteFeeComponent(transactionId: number, feeComponentId: number, transactionType) {
    const endpoint = this.api.getTransactionEndpoint({ 'transaction_type': transactionType });
    this.nonStandardFeeComponentsHolder.nonStandardFeeComponents.forEach((_nonStdFeePeriodComponent, index) => {
      if (feeComponentId === _nonStdFeePeriodComponent.id) {
        this.nonStandardFeeComponentsHolder.nonStandardFeeComponents.splice(index, 1);
        this.nonStandardFeeComponentsSubject.next(this.nonStandardFeeComponentsHolder.nonStandardFeeComponents);
      }
    });
    return this.api.delete(`/api/transactions/${transactionId}/fee_component/${feeComponentId}/`);
  }

  // Fee period / standard fee
  getFeePeriodsByLeaseId(leaseId: number) {
    return this.api.get(`/api/transactions/${leaseId}/fee_period/`)
    .pipe(
      map(result => {
        this.feePeriodComponentsHolder['feePeriodComponents'] = result.fee_period;
        this.feeComponentsSubject.next(this.feePeriodComponentsHolder['feePeriodComponents']);
        return result.fee_period;
      })
    );
  }

  saveLeaseFeePeriodComponent(leaseId: number, feePeriodId: number, feePeriodComponent) {
    this.feePeriodComponentsHolder['feePeriodComponents'].forEach(_feePeriodComponent => {
      if (_feePeriodComponent.id === feePeriodComponent.id) {
        Object.assign(_feePeriodComponent, feePeriodComponent);
        this.feeComponentsSubject.next(this.feePeriodComponentsHolder['feePeriodComponents']);
      }
    });
    return this.api.put(`/api/transactions/${leaseId}/fee_period/${feePeriodId}/`, feePeriodComponent);
  }

  newLeaseFeePeriodComponent(leaseId: number) {
    return this.api.post(`/api/transactions/${leaseId}/fee_period/`, {});
  }

  deleteLeaseFeePeriodComponent(leaseId: number, feePeriodId: number) {
    this.feePeriodComponentsHolder['feePeriodComponents'].forEach((_feePeriodComponent, index) => {
      if (_feePeriodComponent.id === feePeriodId) {
        this.feePeriodComponentsHolder['feePeriodComponents'].splice(index, 1);
        this.feeComponentsSubject.next(this.feePeriodComponentsHolder['feePeriodComponents']);
      }
    });

    return this.api.delete(`/api/transactions/${leaseId}/fee_period/${feePeriodId}/`);
  }

  setFeePeriodComponents(feePeriodComponents?) {
    this.feePeriodComponentsHolder['feePeriodComponents'] = feePeriodComponents;
    this.feeComponentsSubject.next(this.feePeriodComponentsHolder['feePeriodComponents']);
  }

  getFeeTypes(transactionId: number, flags = { 'fee_type': true, 'totals': true }) {
    return this.api.
      get(`/api/transactions/fee_component_standard/${transactionId}/?fee_types=${flags.fee_type}&totals=${flags.totals}`)
      .pipe(
        map(response => {
          if (response) {
            return response;
          }
        })
      );
  }

  getIncentiveFeesTotal(feeComponents: any): any {
    const totals = { total_transaction_amount: 0, total_cbre_amount: 0 };
    feeComponents.map((feeComponent: any) => {
      if (+feeComponent?.override_invoice_amount > 0.00) {
        totals.total_cbre_amount += +feeComponent?.override_invoice_amount;
      } else {
        totals.total_cbre_amount += +feeComponent.amount;
      }
      totals.total_transaction_amount += +feeComponent.transaction_amount;
    });
    return totals;
  }

  getAdditionalFeeTypes(transactionId: number, flags = { 'fee_type': true, 'totals': true }) {
    return this.api.
      // tslint:disable-next-line:max-line-length
      get(`/api/transactions/fee_component_additional_fee/${transactionId}/?fee_types_lease=${flags.fee_type}&totals_lease=${flags.totals}`)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  calculateAvgFeeAnnualRentPeriods(feeAnnualRentPeriods: any[] = [], feeRate: string) {
    const annualRentPeriods = feeAnnualRentPeriods.length;
    const sumAnnualRentPeriods = {
      months: 0,
      annual_transaction_amount: 0,
      fee_rate: 0,
      annual_cbre_fee: 0
    };
    const avgFeeAnnualRentPeriods = {
      months: 0,
      annual_transaction_amount: 0,
      fee_rate: 0,
      annual_cbre_fee: 0
    };
    if (annualRentPeriods > 0) {
      feeAnnualRentPeriods.forEach(annualRentPeriod => {
        sumAnnualRentPeriods.months += +annualRentPeriod.months;
        sumAnnualRentPeriods.annual_transaction_amount += +annualRentPeriod.annual_transaction_amount;
        sumAnnualRentPeriods.fee_rate += +annualRentPeriod.fee_rate;
        sumAnnualRentPeriods.annual_cbre_fee += +annualRentPeriod.annual_cbre_fee;
      });
      avgFeeAnnualRentPeriods.months = sumAnnualRentPeriods.months;
      // tslint:disable-next-line:max-line-length
      avgFeeAnnualRentPeriods.annual_transaction_amount = this.formatAmount(+(sumAnnualRentPeriods.annual_transaction_amount / annualRentPeriods), 2);
      avgFeeAnnualRentPeriods.fee_rate = this.formatAmount(+(sumAnnualRentPeriods.fee_rate / annualRentPeriods), 2);
      if (feeRate === 'percentage') {
        // tslint:disable-next-line:max-line-length
        avgFeeAnnualRentPeriods.annual_cbre_fee = this.formatAmount(+avgFeeAnnualRentPeriods.annual_transaction_amount * +(avgFeeAnnualRentPeriods.fee_rate / 100), 2);
      } else {
        // tslint:disable-next-line:max-line-length
        avgFeeAnnualRentPeriods.annual_cbre_fee = this.formatAmount(((+avgFeeAnnualRentPeriods.annual_transaction_amount / 12) * +avgFeeAnnualRentPeriods.fee_rate), 2);
      }
    } else {
      avgFeeAnnualRentPeriods.months = 0;
      avgFeeAnnualRentPeriods.annual_transaction_amount = 0;
      avgFeeAnnualRentPeriods.fee_rate = 0;
      avgFeeAnnualRentPeriods.annual_cbre_fee = 0;
    }
    return avgFeeAnnualRentPeriods;
  }

  getTotalAdditionalFees(additionalFees) {
    let runningTotal = 0;
    additionalFees.forEach(additionalFee => {
      runningTotal += +additionalFee.fee_amount;
    });
    return +runningTotal;
  }

  formatAmount(value, decimalPlaces) {
    return this.helperService.roundNumber(value, decimalPlaces);
  }

  isCalculationMetricValid(spaceTypes: any[], calculationMetric: string): boolean {
    const spaceTypeSet = new Set();
    if (spaceTypes.length > 0) {
      spaceTypes.forEach((spaceType) => {
        switch (spaceType.property_type_space) {
          case 'land':
            if (calculationMetric === 'size' && +spaceType['land'] !== 0) {
              spaceTypeSet.add(calculationMetric);
            }
          break;
          case 'hotel':
            if (calculationMetric === 'size' && +spaceType['rooms'] !== 0) {
              spaceTypeSet.add(calculationMetric);
            }
          break;
          default:
            if (+spaceType[calculationMetric] !== 0) {
              spaceTypeSet.add(calculationMetric);
            }
          break;
        }
      });
    }
    if (spaceTypeSet.size === 1) {
      return true;
    }
    return false;
  }

}
